import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { useExerciseSession } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment, EffectData } from '@/lib/magnumMoment'
import EffectRenderer from '@/components/EffectRenderer'
import MusicStaff from '@/components/MusicStaff'
import { useMusicPlayback } from '@/hooks/useMusicPlayback'
import {
  generateProgression,
  gradeRomanNumerals,
  type GeneratedProgression,
} from '@/lib/chordGeneration'

type GameState = 'idle' | 'loading' | 'playing' | 'submitted' | 'done'

const QUESTIONS_PER_SESSION = 5
const MAX_PLAYS = 4
const STREAK_MILESTONES = [3, 5]

export default function HarmonicDictationPage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const { sessionId, stats, activeMs, isIdle, startSession, endSession, recordAttempt, dismissIdle } =
    useExerciseSession({ exerciseType: 'HARMONIC_DICTATION' })
  const { playInterval, stop, isPlaying } = useMusicPlayback()

  const [state, setState] = useState<GameState>('idle')
  const [progressions, setProgressions] = useState<GeneratedProgression[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<string[]>([])
  const [focusedIdx, setFocusedIdx] = useState(0)
  const [playsRemaining, setPlaysRemaining] = useState(MAX_PLAYS)
  const [gradeResult, setGradeResult] = useState<{ correct: boolean[]; score: number } | null>(null)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const questionStartRef = useRef(Date.now())
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const difficultyRef = useRef(1.0)
  const staffContainerRef = useRef<HTMLDivElement>(null)
  const [notePositions, setNotePositions] = useState<number[]>([])

  const currentProg = progressions[currentIndex] || null

  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({
        contextType: 'IDLE_RETURN',
        exerciseType: 'HARMONIC_DICTATION',
        category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then(m => magnum.handleEngineResponse(m))
        .catch(() => magnum.say("Still here. Still waiting. Still adorable."))
    }
  }, [isIdle])

  const startGame = async () => {
    setState('loading')
    try {
      const [, diffData] = await Promise.all([
        apiFetch('/music/settings/mine'),
        apiFetch('/game-difficulty/mine/HARMONIC_DICTATION'),
        startSession(),
      ])
      difficultyRef.current = diffData.difficultyLevel
      const progs: GeneratedProgression[] = []
      for (let i = 0; i < QUESTIONS_PER_SESSION; i++) {
        progs.push(generateProgression(diffData.difficultyLevel))
      }
      setProgressions(progs)
      setCurrentIndex(0)
      setAnswers(new Array(progs[0].chords.length).fill(''))
      setFocusedIdx(0)
      setPlaysRemaining(MAX_PLAYS)
      setGradeResult(null)
      setState('playing')
      questionStartRef.current = Date.now()

      fetchMagnumMoment({
        contextType: 'GAME_STARTED',
        exerciseType: 'HARMONIC_DICTATION',
        category: 'MUSIC',
        streak: 0,
        sessionId,
      })
        .then(m => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('idle')
    }
  }

  const handlePlay = async () => {
    if (!currentProg || isPlaying) return
    if (state !== 'submitted' && playsRemaining <= 0) return
    if (state !== 'submitted') setPlaysRemaining(p => p - 1)
    await playInterval(currentProg.abc, 60)
  }

  const handleSubmit = async () => {
    if (!currentProg || state !== 'playing') return
    const result = gradeRomanNumerals(currentProg.chords, answers)
    setGradeResult(result)
    setState('submitted')

    const responseMs = Date.now() - questionStartRef.current
    const correct = result.score >= 0.8

    const newStreak = correct ? stats.streak + 1 : 0
    const isMilestone = correct && STREAK_MILESTONES.includes(newStreak)
    const contextType = correct
      ? isMilestone ? 'STREAK_MILESTONE' : 'CORRECT_ANSWER'
      : 'WRONG_ANSWER'

    fetchMagnumMoment({
      contextType,
      exerciseType: 'HARMONIC_DICTATION',
      category: 'MUSIC',
      correct,
      responseMs,
      activeMs,
      streak: newStreak,
      sessionId,
    })
      .then(moment => {
        magnum.handleEngineResponse(moment, correct ? 'happy' : 'encouraging')
        if (moment.effects?.length) setActiveEffects(moment.effects)
      })
      .catch(() => {})

    await recordAttempt(null, correct, responseMs, {
      score: result.score,
      correctCount: result.correct.filter(Boolean).length,
      totalCount: result.correct.length,
      key: currentProg.key.name,
    })
  }

  const nextProgression = () => {
    stop()
    const next = currentIndex + 1
    if (next >= progressions.length) {
      setState('done')
      fetchMagnumMoment({
        contextType: 'SESSION_END',
        exerciseType: 'HARMONIC_DICTATION',
        category: 'MUSIC',
        streak: stats.streak,
        sessionId,
        activeMs,
      })
        .then(m => magnum.handleEngineResponse(m, 'excited'))
        .catch(() => {})
      endSession()
    } else {
      setCurrentIndex(next)
      const prog = progressions[next]
      setAnswers(new Array(prog.chords.length).fill(''))
      setFocusedIdx(0)
      setPlaysRemaining(MAX_PLAYS)
      setGradeResult(null)
      setState('playing')
      questionStartRef.current = Date.now()
    }
  }

  const setAnswer = useCallback((idx: number, value: string) => {
    setAnswers(prev => {
      const next = [...prev]
      next[idx] = value
      return next
    })
  }, [])

  const handleInputKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const nextIdx = e.shiftKey ? idx - 1 : idx + 1
      if (nextIdx >= 0 && nextIdx < (currentProg?.chords.length ?? 0)) {
        setFocusedIdx(nextIdx)
        inputRefs.current[nextIdx]?.focus()
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (answers.every(a => a.trim().length > 0)) {
        handleSubmit()
      } else {
        const nextEmpty = answers.findIndex((a, i) => i > idx && a.trim() === '')
        if (nextEmpty >= 0) {
          setFocusedIdx(nextEmpty)
          inputRefs.current[nextEmpty]?.focus()
        }
      }
    } else if (e.key === ' ' && state === 'playing') {
      e.preventDefault()
      handlePlay()
    }
  }

  useEffect(() => {
    if (state === 'playing' && inputRefs.current[focusedIdx]) {
      inputRefs.current[focusedIdx]?.focus()
    }
  }, [focusedIdx, state])

  useLayoutEffect(() => {
    if (!staffContainerRef.current || !currentProg) return
    const timer = setTimeout(() => {
      const container = staffContainerRef.current
      if (!container) return
      const noteEls = container.querySelectorAll('.abcjs-note')
      const containerRect = container.getBoundingClientRect()
      const positions: number[] = []
      const count = currentProg.chords.length
      for (let i = 0; i < Math.min(noteEls.length, count); i++) {
        const rect = noteEls[i].getBoundingClientRect()
        positions.push(rect.left + rect.width / 2 - containerRect.left)
      }
      setNotePositions(positions)

      if (state !== 'submitted') {
        for (let i = 0; i < noteEls.length; i++) {
          const isTreble = i < count
          const isFirstChord = isTreble ? i === 0 : i === count
          if (!isFirstChord) {
            (noteEls[i] as HTMLElement).style.visibility = 'hidden'
          }
        }
      }
    }, 50)
    return () => clearTimeout(timer)
  }, [currentProg, currentIndex, state])

  useEffect(() => {
    startGame()
  }, [])

  useEffect(() => {
    if (isIdle && state === 'submitted') dismissIdle()
  }, [state])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">🎹</div>
        <h1 className="text-3xl font-bold">Session Complete!</h1>
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{stats.correct}</p>
            <p className="text-xs text-muted-foreground">Correct</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Play Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  const isPerfect = gradeResult?.score === 1

  return (
    <div className="text-center space-y-6">
      <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

      <div className="flex items-center justify-between">
        <Link to="/practice" className="text-sm text-muted-foreground hover:text-foreground transition">
          &larr; Back
        </Link>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{currentIndex + 1} of {progressions.length}</span>
          <span className="text-primary font-medium">{stats.correct} correct</span>
          {stats.streak >= 3 && <span className="text-amber-500 font-medium">🔥 {stats.streak}</span>}
        </div>
      </div>

      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{
            width: `${((currentIndex + (state === 'submitted' ? 1 : 0)) / progressions.length) * 100}%`,
          }}
        />
      </div>

      {currentProg && (
        <>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              {currentProg.key.name}
            </p>
          </div>

          <div className="bg-white rounded-xl p-3 border border-border" ref={staffContainerRef}>
            <MusicStaff key={`${currentIndex}-${state}`} abc={currentProg.abc} width={500} addClasses />
            <div className="relative h-12 mt-1">
              {currentProg.chords.map((chord, i) => {
                const x = notePositions[i]
                if (x === undefined) return null
                return (
                  <div
                    key={i}
                    className="absolute flex flex-col items-center"
                    style={{ left: x, transform: 'translateX(-50%)' }}
                  >
                    {state === 'submitted' && gradeResult ? (
                      <>
                        <div
                          className={`w-14 h-8 flex items-center justify-center rounded border-2 font-mono text-xs font-bold ${
                            gradeResult.correct[i]
                              ? 'bg-green-100 border-green-500 text-green-800'
                              : 'bg-red-100 border-red-500 text-red-800'
                          }`}
                        >
                          {answers[i] || '—'}
                        </div>
                        {!gradeResult.correct[i] && (
                          <span className="text-[10px] text-green-700 font-mono font-bold">{chord.symbol}</span>
                        )}
                      </>
                    ) : (
                      <input
                        ref={el => { inputRefs.current[i] = el }}
                        type="text"
                        value={answers[i]}
                        onChange={e => setAnswer(i, e.target.value)}
                        onFocus={() => setFocusedIdx(i)}
                        onKeyDown={e => handleInputKeyDown(e, i)}
                        className={`w-14 h-8 text-center rounded border-2 font-mono text-xs font-bold outline-none transition ${
                          focusedIdx === i
                            ? 'border-primary bg-accent'
                            : 'border-border hover:border-primary/50'
                        }`}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handlePlay}
              disabled={isPlaying || (state !== 'submitted' && playsRemaining <= 0)}
              className="px-6 py-3 border-2 border-border rounded-xl hover:border-primary hover:bg-accent transition disabled:opacity-50 flex items-center gap-2"
            >
              <span className="text-2xl">{isPlaying ? '🔊' : '🎵'}</span>
              <span className="text-sm font-medium">
                {state === 'submitted'
                  ? 'Replay'
                  : playsRemaining > 0
                    ? `Play (${playsRemaining} left)`
                    : 'No plays left'}
              </span>
            </button>
          </div>

          {state === 'submitted' && gradeResult ? (
            <>
              <div className={`text-lg font-semibold ${isPerfect ? 'text-primary' : 'text-amber-500'}`}>
                {isPerfect
                  ? 'Perfect!'
                  : `${gradeResult.correct.filter(Boolean).length}/${gradeResult.correct.length} chords correct`}
              </div>

              <div className="flex justify-center gap-3">
                <button
                  onClick={handlePlay}
                  disabled={isPlaying}
                  className="px-5 py-3 border-2 border-border rounded-xl hover:border-primary hover:bg-accent transition disabled:opacity-50 flex items-center gap-2"
                >
                  <span className="text-xl">{isPlaying ? '🔊' : '🎵'}</span>
                  <span className="text-sm font-medium">Replay</span>
                </button>
                <button
                  onClick={nextProgression}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
                >
                  {currentIndex + 1 >= progressions.length ? 'See Results' : 'Next'}
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={answers.every(a => a.trim() === '')}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition disabled:opacity-50"
            >
              Submit
            </button>
          )}
        </>
      )}
    </div>
  )
}
